import { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  usePhoneInput,
  defaultCountries,
  parseCountry,
  FlagImage,
  PhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";
import IntlTelInput, { intlTelInput }  from "@intl-tel-input/react";
import "intl-tel-input/styles";
import { useAuth } from "../../components/AuthContext/AuthContext";
import errorIcon from "../../assets/icons/error-default.svg";
import { OtpFields } from "../../stories/Forms/fields";
import DynamicForm from "../../stories/Forms/DynamicForm";
import { optSchema } from "../../stories/Forms/validationSchema";
import errorImage from "../../assets/icons/error-message-vector.png";
import successImage from "../../assets/icons/success-vector.png";
const initialCountryLookup = async () => {
  const res = await fetch("https://ipapi.co/json");
  const data = await res.json();
  return data.country_code;
};

const PhoneNumberForm = ({ optFields = false, onSubmit, serverError, formikRef, successClass,  initialValues = {
    verifyOtp: "",
    emailMFA: true,
    primary_contact_no:""
  },}) => {
  const auth = useAuth();
  const itiRef = useRef(null);
  const [phone, setPhone] = useState("");
  const [number, setNumber] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  let invalidMsg = null;

  // --- OTP-specific state (mirrors ConfirmOtp.jsx) ---
  const OTP_LENGTH = 6;
  const OTP_INPUT_IDS = ["otp-0", "otp-1", "otp-2", "otp-3", "otp-4", "otp-5"];
  const [otpDigits, setOtpDigits] = useState(() => {
  const digits = (initialValues.verifyOtp || "").split("");
  while (digits.length < OTP_LENGTH) digits.push("");
  return digits.slice(0, OTP_LENGTH);
});
  const otpInputRefs = useRef([]);

  const VALIDATION_ERROR = {
    IS_POSSIBLE: 0,
    INVALID_COUNTRY_CODE: 1,
    TOO_SHORT: 2,
    TOO_LONG: 3,
    IS_POSSIBLE_LOCAL_ONLY: 4,
    INVALID_LENGTH: 5,
  };

useEffect(() => {
  if (initialValues.primary_contact_no) {
    itiRef.current?.getInstance()?.setNumber(initialValues.primary_contact_no);
  }
}, [initialValues.primary_contact_no]);

  const getErrorMessage = (number, errorCode) => {
    if (!number) return "Please enter a number";
    switch (errorCode) {
      case VALIDATION_ERROR.INVALID_COUNTRY_CODE:
        return "Invalid dial code";
      case VALIDATION_ERROR.TOO_SHORT:
        return "Please enter valid number";
      case VALIDATION_ERROR.TOO_LONG:
        return "Please enter valid number";
      default:
        return "Please enter valid number";
    }
  };

  const validationSchema = Yup.object({
    phone: Yup.string()
      .required("Please enter a phone number")
      .test("is-valid-phone", "Enter a valid Indian mobile number", function () {
        if (!isValid) {
          return this.createError({
            message: getErrorMessage(number, errorCode),
          });
        }
        return true;
      }),
  });

  if (showValidation && !isValid) {
    invalidMsg = getErrorMessage(number, errorCode);
  }

  const handleChangeNumber = (newNumber) => {
    setSubmitted(false);
    setNumber(newNumber);
  };

  const handleOtpChange = (index, value, setFieldValue) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    setFieldValue("verifyOtp", newOtp.join(""));

    if (value && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e, setFieldValue) => {
    if (e.key === "Backspace") {
      const newOtp = [...otpDigits];
      if (otpDigits[index]) {
        newOtp[index] = "";
        setOtpDigits(newOtp);
        setFieldValue("verifyOtp", newOtp.join(""));
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtpDigits(newOtp);
        setFieldValue("verifyOtp", newOtp.join(""));
        otpInputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e, setFieldValue) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, OTP_LENGTH);
    if (!/^\d*$/.test(pastedData)) return;

    const newOtp = pastedData.split("");
    while (newOtp.length < OTP_LENGTH) newOtp.push("");
    setOtpDigits(newOtp.slice(0, OTP_LENGTH));
    setFieldValue("verifyOtp", newOtp.slice(0, OTP_LENGTH).join(""));

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    setTimeout(
      () =>
        otpInputRefs.current[
          nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex
        ]?.focus(),
      0
    );
  };

  // Auto-focus first OTP box when this mode mounts
  useEffect(() => {
    if (optFields) {
      otpInputRefs.current[0]?.focus();
    }
  }, [optFields]);

  // ---- OTP verification form ----
  if (optFields) {
    const otpValidationSchema = Yup.object({
      verifyOtp: Yup.string()
        .required("Please enter the OTP")
        .matches(/^\d{6}$/, "Please enter valid OTP"),
    });

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={otpValidationSchema}
        innerRef={formikRef}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, setFieldValue, errors, touched }) => (
          <Form className="phone-number-form">
            <div className="form-group my-0">
              <div className="otp-inputs-wrapper">
                <div className="otp-inputs-grid">
                  {otpDigits.map((digit, index) => (
                    <div key={OTP_INPUT_IDS[index]} className="otp-input-wrapper">
                      <input
                        ref={(el) => (otpInputRefs.current[index] = el)}
                        id={OTP_INPUT_IDS[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) =>
                          handleOtpChange(index, e.target.value, setFieldValue)
                        }
                        onKeyDown={(e) => handleOtpKeyDown(index, e, setFieldValue)}
                        onPaste={(e) => handleOtpPaste(e, setFieldValue)}
                        className={`otp-input ${digit ? "filled" : ""} ${
                           serverError ? "error" : ""
                        } ${successClass ? "success-otp-fields" : ""}`}
                        placeholder=""
                        disabled={isSubmitting}
                        autoComplete="one-time-code"
                        aria-describedby="val1"
                      />
                      <div className="otp-input-underline" />
                    </div>
                  ))}
                </div>
              </div>
              <Field type="hidden" name="emailMFA" />
              {(serverError !== "" || successClass) && ( <span className={successClass ? "valid-mfa" :"Invalid-mfa"}><img src={successClass ? successImage :errorImage}/>{successClass ? "Code verified successfully!" : serverError}</span>)}
            </div>
          </Form>
        )}
      </Formik>
    );
  } 
};

export default PhoneNumberForm;
